import useWidgetFilterNavigator, {
  type FilterCondition,
  type UseWidgetFilterNavigatorOptions,
} from "../../useWidgetFilterNavigator";
import type {
  SeriesHitEvent,
  SeriesClickHandler,
} from "../../Widgets/BarChartUtils";

// Minimal shape of a category entry the hook reads (only `.title`); callers
// pass richer objects (fill/sort) but those are unused here.
interface WidgetClickCategory {
  title?: string;
}

// Item shape the hook reads from `otherData` (only `.category`). Some callers
// pass a non-array placeholder (e.g. `{}`), but `.map` is only reached on the
// non-categories branch where callers pass arrays — preserved as-is.
interface WidgetClickOtherDataItem {
  category?: string | number | null;
}

// Subset of filtersConfig the hook actually reads. NOTE: the hook reads
// `filters_fields_to_override` (snake_case) while callers set
// `filterFieldsToOverride` (camelCase), so this is effectively always
// undefined at runtime — see refactor flag in report. Typed to match what the
// hook reads.
interface WidgetClickFiltersConfig {
  filterToExecute?: string | null;
  // Passed as `null` by every caller (PieChart, CustomBarChart,
  // CustomStackedChart); declared so the object literal type-checks. The hook
  // hardcodes `filterId: null` on the navigator and does not read this key.
  filterId?: string | null;
  scopeToExecute?: string | null;
  scopeId?: string | null;
  // PieChart passes this camelCase key; the hook reads the snake_case
  // `filters_fields_to_override`, so this is unread at runtime (see refactor
  // flag in report). Declared to accept the literal without an excess-property
  // error.
  filterFieldsToOverride?: string[];
  filters_fields_to_override?: string[];
}

export interface UseCustomWidgetClickOptions {
  filterFields?: (string | undefined)[];
  additionalFiltersInDrilldown?: unknown[];
  allowClick?: boolean;
  categories?: Record<string, WidgetClickCategory> | null;
  otherData?: WidgetClickOtherDataItem[] | null;
  filtersConfig?: WidgetClickFiltersConfig;
}

export interface UseCustomWidgetClickReturn {
  handleFilterClick: SeriesClickHandler;
}

const useCustomWidgetClick = ({
  filterFields,
  additionalFiltersInDrilldown = [],
  allowClick,
  categories,
  otherData,
  filtersConfig,
}: UseCustomWidgetClickOptions): UseCustomWidgetClickReturn => {
  const { onFilterClick } = useWidgetFilterNavigator({
    filterToExecute: filtersConfig?.filterToExecute,
    filterId: null,
    scopeToExecute: filtersConfig?.scopeToExecute,
    scopeId: filtersConfig?.scopeId,
    // Runtime-preserved: original forwards this extra key, which
    // UseWidgetFilterNavigatorOptions does not declare and the navigator
    // ignores. Cast avoids an excess-property error without dropping the key.
    filterFieldsToOverride: filtersConfig?.filters_fields_to_override,
  } as UseWidgetFilterNavigatorOptions);

  const handleFilterClick = (
    e: SeriesHitEvent,
    seriesFieldValue: string | null = null
  ) => {
    if (!allowClick || !filterFields?.[0]) {
      return;
    }

    // dataContext is an untyped row datum (Record<string, unknown>); read the
    // category field the same loosely-typed way the original JS did.
    const dataContext = e.target.dataItem.dataContext as {
      category?: unknown;
    };

    let selectedCategory: string[] | undefined;
    if (!!categories) {
      for (let key in categories) {
        if (categories[key].title === dataContext.category) {
          selectedCategory = [key?.toString()];
          break;
        }
      }
    } else {
      selectedCategory =
        dataContext.category === "Other"
          ? otherData?.map((item) => item.category?.toString() as string)
          : [
              (
                dataContext.category as string | undefined
              )?.toString() as string,
            ];
    }

    let selectedSeriesField = [seriesFieldValue?.toString()];

    if (
      !!selectedCategory &&
      // Preserved from JS: compares an array to the string "N/A" (always true);
      // see refactor flag in report.
      (selectedCategory as unknown) !== "N/A" &&
      selectedSeriesField?.[0] !== "N/A"
    ) {
      const filters = [
        ["in", filterFields[0], selectedCategory],
        !!seriesFieldValue && seriesFieldValue !== "other"
          ? ["in", filterFields[1], selectedSeriesField]
          : [],
      ];

      // Runtime-preserved: the original builds a heterogeneous array (filter
      // tuples, an empty array, plus additionalFiltersInDrilldown as a nested
      // element) and forwards it to onFilterClick, which is typed for
      // FilterCondition[]. Cast to match the navigator's parameter type.
      onFilterClick([
        ...filters,
        additionalFiltersInDrilldown,
      ] as unknown as FilterCondition[]);
    }
  };

  return {
    handleFilterClick,
  };
};

export default useCustomWidgetClick;
